import { BASE_STORE_URL } from '@/request/config';
import { getIMEI } from './utils';
import { ExchangeRateToCNYMap, DEFAULT_CURRENCY } from '@/utils/configs/ExchangeRate.config';
import { CartItem } from '@/types/cart';

type TBizReportParams = Partial<
  {
    /** 行为类型，例如曝光、停留、浏览、收藏、下载等. */
    bhvType: 'expose' | 'stay' | 'click' | 'cart' | 'buy';
    bhvValue: number | string;
    bhvDetail: string | number | Record<string, unknown>;
  } & IOpenSearchResponse
>;

/** OpenSearch的返回结果中强绑定的数据 */
interface IOpenSearchResponse {
  /** doc的主键值.该值为开放搜索应用中主表主键值. */
  itemIdList: number[];
  /** 用于标识一个搜索pv, 该值来自开放搜索在搜索结果中返回的request_id的值,原样回传即可. */
  rn: string;
  /** 该值来自开放搜索在搜索结果中返回的ops_request_misc值,原样回传即可. */
  traceInfo: string;
}

// 纯函数请求
const pureRequest = (data: TOpenSearchRequestParams[]) => {
  fetch(BASE_STORE_URL + '/integration/search/dataReporting', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
    keepalive: true,
  });
};

const generateRequestData = (bizParams: TBizReportParams) => {
  try {
    const baseData = {
      // 前端生成device_id，缓存在客户端
      imei: getIMEI(),
      // 行为发生时间
      bhvTime: Date.now(),
      // doc的业务类型
      itemType: 'goods',
      /** 用于区分行为针对的doc是来自哪个搜索服务商输出的结果.如果是来自开放搜索的结果,该字段值设置为Alibaba. */
      traceId: 'Alibaba',
      /** 业务侧用于区分不同业务的一个数值ID.一般是搜索入口,例如有Web端和ios,安卓。如果没有分业务场景,建议填一个default. */
      bizId: 'default',
    };

    return {
      ...bizParams,
      ...baseData,
    };
  } catch (err) {
    console.error(err);
    return bizParams;
  }
};

type TOpenSearchRequestParams = ReturnType<typeof generateRequestData>;

export const searchReport = (bizParams: TBizReportParams) => {
  try {
    const requestData = generateRequestData(bizParams);
    pureRequest([requestData]);
  } catch (err) {
    console.error(err);
  }
};

// 待上报的商品 - session-storage key
const SEARCH_TARGET_SESSION_STORAGE_KEY = 'from_search_product';

/** 在session storage暂存的搜索商品数据 */
export type TStashSearchProductProps = IOpenSearchResponse & {
  /** spuId */
  pid?: string;
  /** skuId */
  cid?: string;
};

/** 暂存搜索跳转的商品信息至session storage */
export function stashTargetProductBeforeRedirect<T>(props: T) {
  try {
    sessionStorage.setItem(SEARCH_TARGET_SESSION_STORAGE_KEY, JSON.stringify(props));
  } catch (err) {
    console.error('stash search product error', err);
  }
}

/** 从session storage获取暂存的商品信息 */
export function popStashedSearchProduct(): TStashSearchProductProps | undefined {
  try {
    const sessionValue = window.sessionStorage.getItem(SEARCH_TARGET_SESSION_STORAGE_KEY);
    const product = sessionValue ? JSON.parse(sessionValue) : undefined;
    console.log('check stash pop product', product);
    return product;
  } catch (err) {
    console.error('parse from search product error', err);
  }

  return undefined;
}

// 队列曝光等待时间
const REPORT_QUEUE_WAIT = 4000;

// 由于商品卡片曝光不能频繁打接口，也和后端说过数据打点的接口必须支持并发，但是后端说没办法
// 所以目前前端维护一个队列上报
export const pushInQueueToReport = (() => {
  let queue: Array<ReturnType<typeof generateRequestData>> = [];
  let timer: number | undefined;
  return (bizParams: TBizReportParams) => {
    // 入队列
    queue.push(generateRequestData(bizParams));

    // 如果当前存在任务，则取消任务
    if (timer) {
      clearTimeout(timer);
      timer = undefined;
    }
    // 设置新任务
    setTimeout(() => {
      if (!queue.length) return;
      // 用队列请求
      pureRequest(queue);
      // 清空队列
      queue = [];
    }, REPORT_QUEUE_WAIT);
  };
})();

interface ICompareParams {
  pid: string;
  cid: string;
  type: 'cart' | 'buy';
  value: string;
  detail: string;
  defer?: boolean;
}

/** 在购物场景中的上报 */
export function reportInPurchase({ pid, cid, type, value, detail }: ICompareParams) {
  try {
    // 先判断有没有from字段
    const fromQuery = new URLSearchParams(window.location.search).get('from') || '';

    // 来自search结果页
    const isFromSearchPage = fromQuery.includes('search');

    // 订单页
    const isOrderConfirmPage = window.location.pathname === '/order/confirm';

    if (isFromSearchPage || isOrderConfirmPage) {
      const stashedProduct = popStashedSearchProduct();

      // 没有暂存商品
      if (!stashedProduct) return;

      // 如果productId都存在，product id都不一致，直接return，注意这里需要转字符串
      if (pid && stashedProduct.pid && `${pid}` !== stashedProduct.pid) return;

      // 对比cid，不一致则return，注意这里需要转字符串
      if (cid && stashedProduct.cid && `${cid}` !== stashedProduct.cid) return;

      const data = {
        ...stashedProduct,
        bhvType: type,
        bhvValue: value,
        bhvDetail: detail,
      };
      // 上报OpenSearch
      searchReport(data);
      console.log('report os success');
    }
  } catch (err) {
    console.error('report to Open Search error', err);
  }
}

/** open-search订单创建上报 */
export function openSearchOrderReport({ items, total }: { items: CartItem[]; total: number }) {
  // os上报
  try {
    const stashedProduct = popStashedSearchProduct();
    // 获取购物车中是否存在搜索跳转的商品
    const cartProduct = items.find((item) => {
      return `${item.commodityId}` === stashedProduct?.cid && `${item.productId}` === stashedProduct?.pid;
    });
    // 有命中商品
    if (cartProduct) {
      // 币种
      const currency = cartProduct.price?.currency || cartProduct.origin_price?.currency || DEFAULT_CURRENCY;
      // 单价
      const amount = total ?? 0;
      // 汇率
      const exchangeRate = ExchangeRateToCNYMap[currency] || ExchangeRateToCNYMap[DEFAULT_CURRENCY];
      // 经过汇率计算的总金额
      const exchangedAmount = amount * exchangeRate;

      // 结账上报
      reportInPurchase({
        pid: `${cartProduct.productId}`,
        cid: `${cartProduct.commodityId}`,
        type: 'buy',
        detail: `buy_price=${exchangedAmount},price_unit=${DEFAULT_CURRENCY}`,
        value: `${items?.length ?? 1}`,
      });
    }
  } catch (err) {
    console.error(err);
  }
}
